import { useCallback, useEffect, useState } from 'react';
import { Flex, Button, theme, Icon, pointingArrowRight, trashcanDelete, Text } from '../../2022-component-library';
import { useTestContext } from '../../context/testContext';
import { useAppointment } from '../../context/appointmentContext';
import { useVideo } from '../../context/videoContext';
import { client } from '../../helpers/api-client';
import { todayString } from '../../helpers/functions';
import styled from '@emotion/styled';
import { useNavigate } from 'react-router-dom';
import { usePostMessage } from '../../context/messageContext';

const ResponsiveCompletedTests = ({ setActiveTab }) => {
  let navigate = useNavigate();
  const { updateTestData } = useTestContext();
  const { appointment } = useAppointment();
  const [testData, setTestData] = useState();
  const dateString = todayString();
  const appointmentId = appointment.currentAppointment.id;
  const { disconnect } = useVideo();
  const postMessage = usePostMessage();

  const StyledTable = styled.table`
    th {
      padding: 0.625rem 1rem;
      background-color: ${theme.colors.primary};
      color: #fff;
      font-size: 0.875rem;
    }
    td {
      text-align: center;
      font-size: 0.75rem;
      padding: 0.625rem;
    }

    tbody tr:nth-of-type(even) {
      background-color: #f1f1f1;
    }
  `;

  const getTestData = useCallback(() => {
    client(`api/appointments/${appointmentId}/`).then(
      ({ tests_results }) => {
        const filteredResults = tests_results?.filter((test) => !test.async_test);
        setTestData(filteredResults);
      },
      (error) => {
        console.error(error);
      },
    );
  }, [appointmentId]);

  useEffect(() => {
    postMessage({ type: 'endOfTest' });
    updateTestData({
      testLeftEye: false,
      testRightEye: false,
      leftEye: null,
      rightEye: null,
      bothEyes: null,
    });
    getTestData();
  }, [postMessage, updateTestData, getTestData]);

  const handleSelectedTest = (testKey, testId) => {
    updateTestData({ testResultsId: testId });
    switch (testKey) {
      case 'crowdedLogmar':
      case 'openLogmar':
      case 'randomE':
      case 'optimaChart':
      case 'kaysOptotypes':
      case 'kaysMultiple':
        return setActiveTab('completedTests');
      case 'contrastSensitivity':
        return setActiveTab('contrastSensitivityResults');
      case 'Refraction':
        return setActiveTab('completedTestRefraction');
      case 'RefractionTwo':
        return setActiveTab('completedTestRefractionTwo');
      default:
        break;
    }
  };

  const updateAppointmentStatus = {
    status: 'completed',
    end: dateString,
  };

  const deleteTest = (testId) => {
    if (window.confirm('Are you sure you want to delete the test?') === true) {
      client(`api/appointment-test-result/${testId}/`, { body: updateAppointmentStatus }, 'DELETE').then(
        () => {
          getTestData();
        },
        (error) => {
          console.error(error);
        },
      );
    }
  };

  const updateStatus = async () => {
    if (window.confirm('Are you sure you want to finish the session? This session will no longer be available.') === true) {
      client(`api/appointments/${appointmentId}/`, { body: updateAppointmentStatus }, 'PATCH').then(
        () => {
          disconnect();
          postMessage({
            type: 'opticianLoggedOut',
            content: true,
          });
          postMessage({
            type: 'endedSession',
            room_name: appointment?.currentAppointment?.room_name,
          });
          navigate(`${process.env.PUBLIC_URL}/error`, {
            state: { error: 'This session has been closed. Close your browser to end the appointment.', title: 'Session closed' },
          });
        },
        (error) => {
          console.error('Error: ', error);
        },
      );
    }
  };

  return testData?.length ? (
    <Flex column centered maxHeight='700px' marginTop='1rem'>
      <Flex overflowY='auto'>
        <StyledTable>
          <thead>
            <tr>
              <th style={{ width: '35vw', maxWidth: '200px' }}>Test Name</th>
              <th>Date</th>
              <th style={{ minWidth: '125px' }}>View Results</th>
              <th style={{ minWidth: '125px' }}>Delete Results</th>
            </tr>
          </thead>
          <tbody>
            {testData.map((test) => (
              <tr key={test.id}>
                <td>{test.test_title}</td>
                <td>{test.submission_date}</td>
                <td>
                  <Icon icon={pointingArrowRight} onClick={() => handleSelectedTest(test.test_key, test.id)} />
                </td>
                <td>
                  <Icon icon={trashcanDelete} onClick={() => deleteTest(test.id)} />
                </td>
              </tr>
            ))}
          </tbody>
        </StyledTable>
      </Flex>
      <Flex marginTop='2rem'>
        <Button onClick={() => updateStatus()}>END SESSION</Button>
      </Flex>
    </Flex>
  ) : (
    <Text>No tests have been completed.</Text>
  );
};

export default ResponsiveCompletedTests;
